import type { Ref } from "vue";
import type { NitroFetchOptions } from "nitropack";
import type { CookieRef } from "#app";
import type { useApiStore } from "#imports";
import {
  unref,
  ref,
  getCmsBaseUrl,
  useCookie,
  useNuxtApp,
  useCurrentCulture,
  useUmbracoPreviewMode,
  useUmbracoPreviewModeApiKey,
  useRequestHeaders,
} from "#imports";

type Dependencies = {
  cookie: CookieRef<any>;
  headerCookies: any;
  route: string;
  culture: Ref<string | null>;
  apiStore: ReturnType<typeof useApiStore>;
  previewMode: Ref<boolean>;
  previewModeApiKey: Ref<string | null>;
};

/**
 * Provides a wrapper around Nuxt's {@link $fetch} to facilitate data fetching to Apollo API endpoints.
 * The following functionality has been added:
 * - Logging and tracing
 * - De-duplicating identical concurrent API calls, e.g. 2x concurrent calls to /api/foo will only make one request
 * - Authentication with cookies & tokens
 * - Handling of cultures
 * - Handling of Umbraco's preview mode
 * @param url The URL to call
 * @param options Any {@link NitroFetchOptions}
 * @param dependencies Any dependencies
 */
export async function $apiFetch<T> ( url: string, options: NitroFetchOptions<string> = {}, dependencies?: Dependencies ): Promise<T> {
  const userAuth = dependencies?.cookie ?? useCookie( "token" );
  const headerCookies = dependencies?.headerCookies ?? useRequestHeaders( ["cookie"] );
  const culture = dependencies?.culture ?? useCurrentCulture();
  const previewModeApiKey = dependencies?.previewModeApiKey ?? useUmbracoPreviewModeApiKey();
  const previewMode = dependencies?.previewMode ?? ref<boolean>( false );
  const route = ref( dependencies?.route || "_unknown_" );

  if ( dependencies === undefined ) {
    const { isPreviewMode } = useUmbracoPreviewMode();
    const nuxtApp = useNuxtApp();

    previewMode.value = isPreviewMode();

    if ( import.meta.server ) {
      route.value = nuxtApp.ssrContext?.url ?? "_unknown_";
    } else {
      route.value = window.location.pathname;
    }
  }

  const defaults: NitroFetchOptions<string> = {
    baseURL: getCmsBaseUrl(),

    onRequest ( { options } ) {
      options.headers = options.headers ?? new Headers();

      // @ts-ignore - headers is a Headers object
      options.headers["Accept-Language"] = culture.value;

      if ( previewMode.value ) {
        // @ts-ignore - headers is a Headers object
        options.headers.Preview = "true";

        // @ts-ignore - headers is a Headers object
        options.headers["Api-Key"] = previewModeApiKey.value;
      }
      // set user token if connected
      if ( userAuth.value ) {
        // @ts-ignore - headers is a Headers object
        options.headers.Authorization = `Bearer ${userAuth.value}`;
      }

      options.headers = {
        ...options.headers,
        ...headerCookies,
      };
    },
  };

  const params = Object.assign( defaults, options );

  return $fetch( unref( url ), params );
}
